/**
 * Year view — 12 mini-calendars in a 4×3 grid with density dots.
 *
 * @module components/calendar/YearView
 */

import { useMemo, memo } from 'react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  addMonths,
  startOfYear,
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { groupEventsByDay, isSameDayTz } from '../../utils/calendarLayout'

const MiniCalendarCell = memo(function MiniCalendarCell({
  day,
  isCurrentMonth,
  isToday,
  hasMeetings,
  meetingCount,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(day)}
      className={`
        relative w-7 h-7 md:w-8 md:h-8 rounded-md flex flex-col items-center justify-center
        text-xs transition-colors
        ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-100'}
        ${isToday ? 'bg-primary-500 text-white hover:bg-primary-600' : ''}
      `}
      title={hasMeetings ? `${meetingCount} meeting${meetingCount > 1 ? 's' : ''}` : undefined}
    >
      <span>{format(day, 'd')}</span>
      {hasMeetings && !isToday && (
        <div
          className={`absolute bottom-0.5 w-1 h-1 rounded-full ${
            meetingCount >= 3 ? 'bg-orange-500' : 'bg-primary-500'
          }`}
        />
      )}
      {hasMeetings && isToday && (
        <div className="absolute bottom-0.5 w-1 h-1 rounded-full bg-white" />
      )}
    </button>
  )
})

function MiniCalendar({ monthDate, userTz, dayMap, today, onDayClick, onMonthClick }) {
  const days = useMemo(() => {
    const ms = startOfMonth(monthDate)
    const me = endOfMonth(monthDate)
    return eachDayOfInterval({ start: startOfWeek(ms), end: endOfWeek(me) })
  }, [monthDate])

  return (
    <div>
      <button
        type="button"
        onClick={() => onMonthClick(monthDate)}
        className="text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors mb-2"
      >
        {format(monthDate, 'MMMM')}
      </button>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-0 mb-0.5">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center text-[10px] text-gray-400 w-7 md:w-8">
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-0">
        {days.map((day, idx) => {
          const key = format(day, 'yyyy-MM-dd')
          const dayMeetings = dayMap.get(key)
          const isCurrentMonth = isSameMonth(day, monthDate)
          const isToday = isSameDayTz(day, today, userTz)

          return (
            <MiniCalendarCell
              key={idx}
              day={day}
              isCurrentMonth={isCurrentMonth}
              isToday={isToday}
              hasMeetings={!!dayMeetings}
              meetingCount={dayMeetings?.length ?? 0}
              onClick={onDayClick}
            />
          )
        })}
      </div>
    </div>
  )
}

export default function YearView({
  meetings,
  currentDate,
  userTz,
  onDayClick,
  onMonthClick,
}) {
  const dateInTz = utcToZonedTime(currentDate, userTz)

  // 12 months starting from Jan of the current year
  const months = useMemo(() => {
    const yearStart = startOfYear(dateInTz)
    return Array.from({ length: 12 }, (_, i) => addMonths(yearStart, i))
  }, [dateInTz])

  // Single grouping pass for the entire year
  const dayMap = useMemo(() => groupEventsByDay(meetings, userTz), [meetings, userTz])

  const today = utcToZonedTime(new Date(), userTz)

  return (
    <div className="glass rounded-2xl p-4 lg:p-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
        {months.map((monthDate, i) => (
          <MiniCalendar
            key={i}
            monthDate={monthDate}
            userTz={userTz}
            dayMap={dayMap}
            today={today}
            onDayClick={onDayClick}
            onMonthClick={onMonthClick}
          />
        ))}
      </div>
    </div>
  )
}
